import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtError, fmtDateTime, fmtMoney } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Copy, Trash, ArrowUpRight, MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import EditTemplateModalLazy from "@/components/EditTemplateModal";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Templates() {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [templates, setTemplates] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const [t, c] = await Promise.all([
                api.get("/campaign-templates"),
                api.get("/clients"),
            ]);
            setTemplates(t.data || []);
            setClients(c.data || []);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter((t) =>
            (t.name || "").toLowerCase().includes(q) ||
            (t.description || "").toLowerCase().includes(q) ||
            (t.client_org_name || "").toLowerCase().includes(q),
        );
    }, [templates, query]);

    const onDelete = async (t) => {
        const ok = await confirm({
            title: "Delete this template?",
            description: `"${t.name}" will be removed. Campaigns already created from it are unaffected.`,
            confirmText: "Delete template",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/campaign-templates/${t.id}`);
            toast.success("Template deleted");
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    const onDuplicate = async (t) => {
        const name = window.prompt("Name for the duplicate", `${t.name} (copy)`);
        if (!name || !name.trim()) return;
        try {
            await api.post("/campaign-templates", {
                name: name.trim(),
                description: t.description ? `${t.description} (duplicated)` : null,
                snapshot: t.snapshot,
            });
            toast.success("Template duplicated");
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    const onUse = (t) => {
        navigate(`/campaigns/new?template=${t.id}`);
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Catalog</div>
                    <h1 className="font-display text-4xl tracking-tighter">Campaign templates</h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        Reusable intake snapshots — pre-fill the campaign create form in one click. Save from any campaign's Overview tab.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search templates…"
                            data-testid="templates-search"
                            className="rounded-none border-slate-300 pl-8 h-10 w-64"
                        />
                    </div>
                    <Link
                        to="/campaigns/new"
                        data-testid="templates-create-campaign"
                        className="bg-slate-900 text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-800"
                    >
                        <Plus size={14} weight="bold" /> New campaign
                    </Link>
                </div>
            </header>

            {loading && <p className="text-xs font-mono text-slate-400">loading…</p>}

            {!loading && templates.length === 0 && (
                <div className="border border-dashed border-slate-300 p-12 text-center" data-testid="templates-empty">
                    <Copy size={32} className="text-slate-400 mx-auto" />
                    <h2 className="font-display text-2xl mt-3">No templates yet</h2>
                    <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                        Open any campaign's Overview tab and click <b>Save as template</b> to capture its intake settings here. Future campaigns can be pre-filled in one click.
                    </p>
                </div>
            )}

            {!loading && templates.length > 0 && filtered.length === 0 && (
                <p className="text-sm text-slate-500">No templates match &ldquo;{query}&rdquo;.</p>
            )}

            {!loading && filtered.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="templates-grid">
                    {filtered.map((t) => (
                        <TemplateCard
                            key={t.id}
                            t={t}
                            onEdit={() => setEditing(t)}
                            onDuplicate={() => onDuplicate(t)}
                            onDelete={() => onDelete(t)}
                            onUse={() => onUse(t)}
                        />
                    ))}
                </div>
            )}

            {editing && (
                <EditTemplateModalLazy
                    template={editing}
                    clients={clients}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); toast.success("Template updated"); }}
                />
            )}
        </div>
    );
}

function TemplateCard({ t, onEdit, onDuplicate, onDelete, onUse }) {
    const s = t.snapshot || {};
    const totalMoneyHint = s.revenue ? fmtMoney(s.revenue, s.currency) : null;
    return (
        <div className="bg-white p-5 flex flex-col" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                    <div className="font-display text-lg truncate">{t.name}</div>
                    {t.client_org_name && (
                        <div className="overline mt-0.5 truncate">{t.client_org_name}</div>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button
                        type="button"
                        onClick={onEdit}
                        title="Edit"
                        data-testid={`template-edit-${t.id}`}
                        className="p-1.5 text-slate-500 hover:text-slate-900"
                    >
                        <PencilSimple size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={onDuplicate}
                        title="Duplicate"
                        data-testid={`template-duplicate-${t.id}`}
                        className="p-1.5 text-slate-500 hover:text-slate-900"
                    >
                        <Copy size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        title="Delete"
                        data-testid={`template-delete-${t.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-700"
                    >
                        <Trash size={14} />
                    </button>
                </div>
            </div>

            {t.description && (
                <p className="text-xs text-slate-600 mb-3 line-clamp-2">{t.description}</p>
            )}

            <div className="grid grid-cols-2 gap-px bg-slate-200 border border-slate-200 mb-3">
                <Stat label="Pricing" value={s.pricing_model || "—"} />
                <Stat label="Creative" value={s.creative_type || "—"} />
                <Stat label="Volume" value={s.volume ? Number(s.volume).toLocaleString() : "—"} />
                <Stat label="Revenue" value={totalMoneyHint || "—"} />
            </div>

            <div className="overline text-slate-400 mb-3" style={{ fontSize: 9 }}>
                created by {t.created_by_name} · {fmtDateTime(t.created_at)}
                {t.updated_at && t.updated_at !== t.created_at && (
                    <span> · updated {fmtDateTime(t.updated_at)}</span>
                )}
            </div>

            <div className="mt-auto">
                <Button
                    onClick={onUse}
                    data-testid={`template-use-${t.id}`}
                    className="rounded-none bg-slate-900 hover:bg-slate-800 text-white w-full gap-2"
                >
                    Use template <ArrowUpRight size={14} weight="bold" />
                </Button>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white p-2">
            <div className="overline" style={{ fontSize: 9 }}>{label}</div>
            <div className="font-mono text-xs mt-0.5 truncate" title={String(value)}>{value}</div>
        </div>
    );
}
